import type { Translation } from '../../types';
import type { work as en } from '../en/work';

export const work: Translation<typeof en> = {
  crumbs: { compliance: 'Tuân thủ', queue: 'Việc cần xử lý', settings: 'Tích hợp' },

  kind: {
    UPSTREAM_REGISTRANT_UNVERIFIED: 'Chủ thể chưa xác minh với nhà đăng ký tài trợ',
    EKYC_MISMATCH: 'eKYC không khớp',
    EKYC_FAILED: 'eKYC không đạt',
    VERIFICATION_INCONCLUSIVE: 'Xác thực chưa đủ căn cứ',
    DOSSIER_MANUAL_REVIEW: 'Chính sách yêu cầu duyệt thủ công',
    SUBJECT_CHANGED_AFTER_DOSSIER: 'Chủ thể thay đổi sau khi hồ sơ hoàn tất',
    VNNIC_SYNC_MISSING: 'Chưa đồng bộ VNNIC',
    VNNIC_SYNC_STUCK: 'Đồng bộ VNNIC bị treo',
    VNNIC_SYNC_REJECTED: 'VNNIC từ chối / yêu cầu sửa',
    VNNIC_SYNC_DRIFT: 'Hồ sơ khác bản đã gửi VNNIC',
    DECLARATION_REVIEW: 'Bản khai cần duyệt',
    MANUAL_ACTIVATION: 'Kích hoạt thủ công (.vn)',
    WORKFLOW_FAILED: 'Đăng ký / gia hạn lỗi',
    WORKFLOW_MANUAL_REVIEW: 'Quy trình chờ người xử lý',
    REGISTRY_TRANSACTION: 'Lệnh registry chưa rõ kết quả',
    RECONCILIATION_ISSUE: 'Lệch dữ liệu với registry',
    VNNIC_LIST_MISMATCH: 'Lệch danh sách VNNIC',
    COVERAGE_GAP: 'Khoảng trống tuân thủ',
    NOTIFICATION_FAILED: 'E-mail gửi lỗi',
    WEBHOOK_ENDPOINT_DISABLED: 'Webhook bị tắt',
    HOLDER_TRANSFER_REVIEW: 'Duyệt hồ sơ chuyển nhượng',
    SUBJECT_CONVERSION_REVIEW: 'Duyệt hồ sơ chuyển đổi chủ thể',
    NAME_CORRECTION_REVIEW: 'Duyệt sửa tên chủ thể',
    VNNIC_PANEL_NAME_CHANGE: 'Nhập đổi tên trên hệ thống VNNIC',
    VNNIC_PANEL_REGISTRY_LOCK: 'Khoá tên miền trên hệ thống VNNIC',
    VNNIC_PANEL_REGISTRY_UNLOCK: 'Mở khoá tên miền trên hệ thống VNNIC',
    VNNIC_PANEL_OVERDUE: 'Thay đổi trên hệ thống VNNIC quá hạn',
    REGISTRAR_SIGNATURE_MISMATCH: 'File ký của NĐK không khớp hồ sơ',
    REGISTRAR_SIGNATURE_PRECHECK: 'Hồ sơ chưa qua kiểm tra trước khi NĐK ký',
    POST_OPERATION_ATTENTION: 'Bước sau nghiệp vụ bị từ chối / lỗi',
    ADDON_ATTENTION: 'Tiện ích cần nhân viên xem',
    EINVOICE_RUN_FAILED: 'Hoá đơn điện tử chưa phát hành được ở lượt 17h',
    PROTECTED_CHANGE_PENDING: 'Tên miền bảo vệ: thay đổi chờ xác nhận',
    PROTECTED_DOMAIN_EXPIRY: 'Tên miền bảo vệ: giai đoạn hết hạn',
    PROTECTED_TRANSFER_OUT: 'Tên miền bảo vệ: yêu cầu chuyển đi',
  },
  severity: { CRITICAL: 'Nghiêm trọng', HIGH: 'Cao', MEDIUM: 'Trung bình', LOW: 'Thấp' },
  status: { OPEN: 'Đang mở', RESOLVED: 'Đã xử lý', AUTO_RESOLVED: 'Hệ thống tự đóng', CLOSED: 'Đã đóng' },
  resolution: {
    APPROVED: 'Đã duyệt',
    REJECTED: 'Từ chối',
    CORRECTION_REQUESTED: 'Yêu cầu bổ sung, chỉnh sửa',
    DONE: 'Đã xử lý',
    AUTO_RESOLVED: 'Vấn đề không còn',
  },
  field: { MATCH: 'Khớp', MISMATCH: 'Không khớp', UNREADABLE: 'Không đọc được', NOT_DECLARED: 'Chưa khai' },
  fieldName: { name: 'Họ và tên', document_number: 'Số giấy tờ', birth: 'Ngày sinh' },
  sourceKind: {
    workflow: 'Quy trình',
    registry_transaction: 'Lệnh registry',
    registry_transaction_group: 'Các lệnh registry',
    reconciliation_issue: 'Đối soát registry',
    dossier: 'Hồ sơ tên miền',
    dossier_sync: 'Đồng bộ hồ sơ VNNIC',
    vnnic_list_mismatch: 'Đối chiếu danh sách VNNIC',
    coverage_gap: 'Độ phủ tuân thủ',
    notification: 'Nhật ký e-mail',
    webhook_endpoint: 'Webhook',
    name_change_request: 'Yêu cầu đổi tên',
    declaration_submission: 'Bản khai',
    panel_operation: 'Thao tác trên hệ thống VNNIC',
    subject_verification: 'Xác minh chủ thể',
  },
  // A work item's finding as a sentence (lib/workSummary.ts), from its kind and data.
  summary: {
    code: ' (mã {{code}})',
    vnnicRejected: { REJECTED: 'VNNIC từ chối hồ sơ{{code}}.', CORRECTION_REQUIRED: 'VNNIC yêu cầu sửa, bổ sung hồ sơ{{code}}.' },
    VNNIC_SYNC_MISSING: 'Hồ sơ đã hoàn tất nhưng chưa được đưa vào hàng gửi VNNIC.',
    VNNIC_SYNC_DRIFT: 'Nội dung hồ sơ đã khác bản đã gửi VNNIC.',
    VNNIC_SYNC_STUCK: 'Đồng bộ VNNIC đứng ở trạng thái “{{status}}” quá lâu{{code}}.',
    DOSSIER_MANUAL_REVIEW: 'Chính sách đang áp dụng yêu cầu nhân viên duyệt hồ sơ.',
    WORKFLOW_FAILED: '{{workflow}} lỗi ở bước “{{step}}”{{code}}.',
    WORKFLOW_MANUAL_REVIEW: '{{workflow}} dừng ở bước “{{step}}”, chờ người xử lý{{code}}.',
    registryTransaction: 'Lệnh “{{operation}}” chưa rõ kết quả tại registry; đối soát sẽ xác định.',
    registryTransactionGrouped: '{{count}} lệnh chưa rõ kết quả tại registry {{registry}}.',
    reconciliation: {
      NAMESERVER: 'Name server trong hệ thống khác với registry.',
      EXISTENCE: 'Tên miền chỉ có ở một phía (hệ thống hoặc registry).',
      SPONSORSHIP: 'Registry ghi một nhà đăng ký khác đang quản lý tên miền.',
      other: 'Dữ liệu lệch với registry ({{type}}).',
    },
    COVERAGE_GAP: 'Khoảng trống tuân thủ: {{gap}}.',
    VNNIC_LIST_MISMATCH: 'Danh sách tên miền của VNNIC lệch với hệ thống: {{mismatch}}.',
    NOTIFICATION_FAILED: 'E-mail “{{template}}” gửi không thành công sau {{attempts}} lần.',
    VNNIC_PANEL_REGISTRY_LOCK: 'Đặt các trạng thái khoá tên miền trên hệ thống quản lý của VNNIC.',
    VNNIC_PANEL_REGISTRY_UNLOCK: 'Gỡ các trạng thái khoá tên miền trên hệ thống quản lý của VNNIC.',
    workflow: { DOMAIN_REGISTRATION: 'Đăng ký tên miền', DOMAIN_RENEWAL: 'Gia hạn tên miền', DOMAIN_TRANSFER_IN: 'Chuyển tên miền về', DOMAIN_RESTORE: 'Khôi phục tên miền' },
    step: {
      dossier: 'hồ sơ',
      gate: 'kiểm tra tuân thủ',
      zone: 'quy định đuôi tên miền',
      prepare: 'chuẩn bị',
      contact: 'tạo contact tại registry',
      registry_create: 'tạo tại registry',
      registry_renew: 'gia hạn tại registry',
      await_approval: 'chờ registry duyệt',
      activate: 'kích hoạt',
      compensate: 'hoàn tác',
      rollback: 'hoàn tác',
      abandon: 'huỷ',
      unhold: 'giải phóng tiền giữ',
    },
    operation: {
      domain_check: 'kiểm tra tên miền',
      domain_create: 'tạo tên miền',
      domain_info: 'đọc thông tin tên miền',
      domain_update: 'cập nhật tên miền',
      domain_update_status: 'đổi trạng thái tên miền',
      domain_update_registrant: 'đổi chủ thể',
      domain_update_authinfo: 'đổi mã chuyển',
      domain_renew: 'gia hạn',
      domain_delete: 'xoá tên miền',
      domain_restore: 'khôi phục',
      domain_transfer_request: 'yêu cầu chuyển về',
      domain_transfer_query: 'hỏi trạng thái chuyển',
      domain_transfer_approve: 'đồng ý chuyển đi',
      domain_transfer_reject: 'từ chối chuyển đi',
      domain_transfer_cancel: 'huỷ chuyển về',
      contact_create: 'tạo contact',
      contact_info: 'đọc contact',
      host_create: 'tạo name server',
      host_update: 'cập nhật name server',
    },
    issue: { NAMESERVER: 'name server', EXISTENCE: 'tồn tại', SPONSORSHIP: 'nhà đăng ký quản lý', UNKNOWN_OUTCOME: 'lệnh chưa rõ kết quả' },
    gap: {
      MISSING_DOSSIER: 'tên miền chưa có hồ sơ',
      INCOMPLETE_DOSSIER: 'hồ sơ chưa hoàn chỉnh',
      VERIFICATION_NOT_VALID: 'xác minh chủ thể không hợp lệ',
      SUBJECT_MISMATCH: 'chủ thể không khớp',
      VNNIC_SYNC_MISSING: 'chưa đồng bộ VNNIC',
      VNNIC_SYNC_PENDING: 'đang chờ đồng bộ VNNIC',
      VNNIC_SYNC_REJECTED: 'VNNIC từ chối đồng bộ',
      VNNIC_SYNC_ERROR: 'lỗi đồng bộ VNNIC',
      REPORT_PROJECTION_MISSING: 'thiếu trong tập báo cáo',
      VNNIC_REGISTRY_MISSING: 'không có trong danh sách VNNIC',
    },
    mismatch: { MISSING_AT_VNNIC: 'thiếu ở VNNIC', UNKNOWN_LOCALLY: 'VNNIC có, hệ thống không có' },
    syncStatus: { QUEUED: 'chờ gửi', SUBMITTING: 'đang gửi', SUBMITTED: 'đã gửi, chờ VNNIC', ACKNOWLEDGED: 'VNNIC đã nhận', REJECTED: 'bị từ chối', CORRECTION_REQUIRED: 'cần sửa' },
  },

  queue: {
    title: 'Việc cần xử lý',
    subtitle: 'Mọi việc cần người xử lý: các hồ sơ hệ thống không tự quyết và các ngoại lệ vận hành.',
    note: 'Hệ thống chỉ tự hoàn tất hồ sơ khi mọi thông tin đều khớp. Không có gì ở đây bị tự động từ chối hay huỷ; đơn đăng ký đang chờ một hồ sơ cần duyệt sẽ không bị huỷ khi hết hạn. Các mục tự đóng khi vấn đề không còn.',
    stats: { open: 'Đang mở', mine: 'Giao cho tôi', unassigned: 'Chưa giao', critical: 'Nghiêm trọng / cao' },
    byKind: 'Đang mở theo loại',
    filter: {
      status: 'Trạng thái',
      kind: 'Loại',
      severity: 'Mức độ',
      assignee: 'Người xử lý',
      age: 'Mở lâu hơn',
      any: 'Tất cả',
      me: 'Tôi',
      unassigned: 'Chưa giao',
      hours: '{{count}} giờ',
      days: '{{count}} ngày',
      scoped: 'Đang chỉ hiển thị các mục của một tên miền hoặc một chủ thể.',
      clear: 'Hiện tất cả',
    },
    col: { kind: 'Loại', severity: 'Mức độ', subject: 'Liên quan', summary: 'Phát hiện', opened: 'Mở lúc', assignee: 'Người xử lý', state: 'Tình trạng' },
    empty: 'Không có việc nào cần người xử lý.',
    noDomain: 'Không gắn tên miền',
  },

  item: {
    title: 'Việc cần xử lý',
    loadError: 'Không tải được mục này.',
    facts: 'Phát hiện',
    comparison: 'Đối chiếu với thông tin đã khai',
    comparisonNote: 'Hệ thống chỉ lưu kết quả từng trường — không bao giờ lưu giá trị đọc được từ giấy tờ tuỳ thân.',
    face: 'So khớp khuôn mặt',
    faceBand: 'Mức',
    similarity: 'Độ tương đồng',
    threshold: 'Ngưỡng',
    reasonCodes: 'Mã lý do',
    changed: 'Phần đã thay đổi',
    kind: 'Loại',
    severity: 'Mức độ',
    status: 'Trạng thái',
    source: 'Nguồn',
    openSource: 'Mở trang nguồn',
    openedBy: 'Nguồn báo',
    opened: 'Mở lúc',
    assignee: 'Người xử lý',
    assignedAt: 'Giao lúc',
    firstAction: 'Xử lý đầu tiên',
    domain: 'Tên miền',
    dossier: 'Hồ sơ',
    subject: 'Chủ thể',
    openSubject: 'Mở chủ thể',
    openRequest: 'Mở yêu cầu',
    due: 'Hạn trả lời',
    decidedOnRequest: 'Việc này được quyết định trên yêu cầu (duyệt / từ chối / yêu cầu bổ sung, hoặc bước nhập trên hệ thống VNNIC). Việc tự đóng khi cơ quan quản lý ghi nhận thay đổi.',
    verification: 'Xác thực',
    decision: 'Quyết định',
    decidedBy: 'Người đóng',
    decidedAt: 'Đóng lúc',
    reason: 'Lý do',
    effect: 'Kết quả',
    system: 'Hệ thống',
    assignMe: 'Nhận việc',
    assignOther: 'Giao cho…',
    approve: 'Duyệt',
    reject: 'Từ chối',
    resolve: 'Đóng việc',
    raw: 'Toàn bộ dữ kiện đã ghi',
  },

  assign: {
    title: 'Giao việc',
    label: 'Nhân viên',
    submit: 'Giao',
  },
  approve: {
    title: 'Duyệt',
    description: {
      identity: 'Nhân viên không xác thực chủ thể bằng tay: chủ thể chỉ được xác thực bằng eKYC VNNIC hoặc tài liệu ký số. Hãy từ chối / yêu cầu chủ thể làm lại eKYC hoặc ký số bản khai.',
      dossier: 'Hoàn tất hồ sơ với bạn là người duyệt. Mọi yêu cầu khác phải đã đầy đủ; sau đó đơn đăng ký tiếp tục và vẫn phải qua ComplianceGate.',
      other: 'Ghi nhận phát hiện. Không có gì thay đổi trên tên miền hay hồ sơ.',
    },
    submit: 'Duyệt',
  },
  reject: {
    title: 'Từ chối',
    description: 'Trả hồ sơ lại cho chủ thể (CORRECTION_REQUIRED) và gửi e-mail báo cho họ. Đơn đăng ký tiếp tục chờ đến khi chủ thể chỉnh sửa hoặc hết hạn.',
    descriptionNoDossier: 'Ghi nhận việc từ chối. Không có hồ sơ nào để trả lại.',
    note: 'Lời nhắn cho chủ thể (không bắt buộc)',
    noteHint: 'Hiển thị trong e-mail. Không ghi số giấy tờ tuỳ thân.',
    submit: 'Từ chối',
  },
  resolve: {
    title: 'Đóng việc',
    description: 'Đóng mục này với lý do của bạn. Việc này không thay đổi tên miền, đơn hàng hay registry: hãy xử lý ở trang nguồn trước.',
    submit: 'Đóng việc',
  },
  form: {
    reason: 'Lý do (nội bộ, được ghi kiểm toán)',
    reasonHint: 'Bắt buộc. Được lưu vào nhật ký kiểm toán cùng tên bạn.',
    done: 'Đã xong.',
  },

  linked: {
    title: 'Việc cần xử lý',
    open_one: '{{count}} việc đang mở',
    open_other: '{{count}} việc đang mở',
    none: 'Không có việc nào đang mở.',
    view: 'Xem các việc',
  },

  webhooks: {
    title: 'Webhook',
    platformOnly: 'chỉ nền tảng',
    groups: {
      work_item: 'Việc cần xử lý', domain: 'Tên miền', addon: 'Dịch vụ tên miền', registrant_change: 'Đổi tên / chuyển nhượng (.vn)',
      registry_lock: 'Khoá tại VNNIC', transfer_in: 'Chuyển tên miền về', transfer_out: 'Chuyển tên miền đi', registrant: 'Chủ thể (EPP)',
      restore: 'Khôi phục', contact: 'Contact tại registry', subject: 'Chủ thể', verification: 'Xác minh', dossier: 'Hồ sơ',
      vnnic_sync: 'Đồng bộ VNNIC', order: 'Đơn hàng', invoice: 'Hoá đơn', quote: 'Báo giá', webhook: 'Webhook',
    },
    subtitle: 'Sự kiện gửi tới hệ thống của bạn (ví dụ CRM), có ký bằng khoá bí mật.',
    customerTitle: 'Webhook của khách',
    customerSubtitle: 'Các endpoint của khách hàng này. Chúng chỉ nhận sự kiện về đối tượng thuộc khách hàng.',
    add: 'Thêm endpoint',
    edit: 'Sửa endpoint',
    empty: 'Chưa có endpoint nào.',
    col: { endpoint: 'Endpoint', events: 'Sự kiện', state: 'Tình trạng', lastSuccess: 'Gửi thành công gần nhất' },
    allEvents: 'Mọi sự kiện',
    enabled: 'Đang bật',
    disabled: 'Đang tắt',
    autoDisabled: 'Bị tắt tự động',
    failing_one: '{{count}} lần gửi lỗi liên tiếp',
    failing_other: '{{count}} lần gửi lỗi liên tiếp',
    disableNote: 'Endpoint gửi lỗi {{failures}} lần liên tiếp trong ít nhất {{hours}} giờ sẽ bị tắt tự động; các sự kiện đang chờ sẽ được gửi khi bật lại.',
    secretSet: 'Đã đặt khoá bí mật',
    secretNone: 'Chưa có khoá bí mật: sự kiện không được ký',
    test: 'Gửi sự kiện thử',
    testQueued: 'Đã xếp sự kiện thử vào hàng gửi.',
    form: {
      description: 'Mô tả',
      url: 'URL',
      urlHint: 'Dùng https. Trả lời 2xx trong vòng 10 giây.',
      events: 'Sự kiện',
      all: 'Mọi sự kiện dành cho chủ sở hữu này',
      enabled: 'Bật',
      secret: 'Khoá ký',
      secretKeep: 'Giữ khoá hiện tại',
      secretSet: 'Đặt khoá mới',
      secretClear: 'Bỏ khoá (không ký)',
      secretHint: 'Từ 16 đến 256 ký tự. Khoá không bao giờ được hiển thị lại; hãy lưu ở hệ thống nhận.',
      save: 'Lưu',
    },
    log: {
      title: 'Nhật ký gửi',
      col: { event: 'Sự kiện', endpoint: 'Endpoint', status: 'Trạng thái', attempts: 'Số lần gửi', last: 'Phản hồi gần nhất', created: 'Tạo lúc' },
      empty: 'Chưa gửi sự kiện nào.',
      resend: 'Gửi lại',
      resent: 'Đã xếp gửi lại.',
      body: 'Nội dung đã gửi',
      status: { PENDING: 'Chờ gửi', SENDING: 'Đang gửi', DELIVERED: 'Đã gửi', FAILED: 'Thất bại' },
    },
  },
};
